import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

const cardBase =
  'rounded-card border border-v-outline bg-v-surface p-6 shadow-[0_1px_3px_rgb(0_0_0/0.05)]';

interface EstadoVacioProps {
  icono: LucideIcon;
  titulo: string;
  descripcion: string;
  /** Texto de la accion principal. Si no se pasa, no se renderiza boton. */
  accion?: string;
  onAccion?: () => void;
  /** Acciones secundarias (enlaces a otras areas). */
  alternativas?: ReactNode;
  /** Contenedor/slot del icono. */
  container?: string;
  ink?: string;
}

/**
 * Estado sin contenido.
 *
 * Cubre el hueco que dejaron `EjerciciosPage` y `EvaluacionPage`: cuando la API
 * devuelve `[]` (tabla vacia en la base de datos) pintaban una rejilla sin
 * tarjetas y sin decir nada, que el estudiante lee como "la app esta rota".
 * Aqui se explica que pasa y se ofrece un siguiente paso.
 *
 * Mismo lenguaje visual que la Fase 4: container-100 con ink-700, nunca texto
 * blanco sobre el fill 500 (blanco sobre rosa es 3.5:1).
 */
export function EstadoVacio({
  icono: Icono,
  titulo,
  descripcion,
  accion,
  onAccion,
  alternativas,
  container = '#B8F5EC',
  ink = '#00574C',
}: EstadoVacioProps) {
  return (
    <section className={`${cardBase} mx-auto w-full max-w-md text-center`}>
      <span
        className="mx-auto grid h-14 w-14 place-items-center rounded-2xl"
        style={{ backgroundColor: container }}
      >
        <Icono className="h-7 w-7" style={{ color: ink }} aria-hidden />
      </span>

      <h2 className="mt-4 text-lg font-extrabold text-v-on-surface">{titulo}</h2>
      <p className="mt-1.5 text-sm leading-relaxed text-v-on-surface-variant">{descripcion}</p>

      {accion && onAccion && (
        /* Mint solido + tinta oscura, no el degradado de marca con blanco:
           sobre #00BFA6 el blanco da 2.33:1 y a 14px se necesitan 4.5:1.
           #003B34 sobre #00BFA6 da 5.37:1. */
        <button
          type="button"
          onClick={onAccion}
          className="mt-4 min-h-12 w-full rounded-pill px-5 text-sm font-bold text-[#003B34]
                     transition-transform active:scale-[0.98]"
          style={{ backgroundColor: '#00BFA6' }}
        >
          {accion}
        </button>
      )}

      {alternativas && <div className="mt-4 space-y-2 text-left">{alternativas}</div>}
    </section>
  );
}

/** Enlace secundario reutilizable dentro de `EstadoVacio`. */
export function EnlaceAlternativo({
  icono: Icono,
  texto,
  onClick,
  container = '#DBEAFE',
  ink = '#1E40AF',
}: {
  icono: LucideIcon;
  texto: string;
  onClick: () => void;
  container?: string;
  ink?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-12 w-full items-center gap-3 rounded-card border
                 border-v-outline px-4 text-left text-sm font-semibold text-v-on-surface
                 transition-colors active:bg-black/5"
    >
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
        style={{ backgroundColor: container }}
      >
        <Icono className="h-5 w-5" style={{ color: ink }} aria-hidden />
      </span>
      {texto}
    </button>
  );
}
